import React, { useState, useEffect } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect';
import academicTermsApis from '../../../apis/academicTerms';
import {
  AcademicYearTermsRequestBody,
  EditAcademicTermModalProps
} from '../types';
import TextInputWithIcon from '../../../components/TextInputWithIcon';

const EditAcademicTermModal: React.FC<EditAcademicTermModalProps> = ({
  open,
  onClose,
  fetchData,
  onApiSuccessAndFailure,
  academicTerm,
  academicYear,
  grade,
  division,
  school,
  previousAcademicTerms
}) => {
  const [termId, setTermId] = useState('');
  const [orderSequence, setOrderSequence] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [availableTerms, setAvailableTerms] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    if (open && academicTerm) {
      setTermId(academicTerm.id);
      setOrderSequence(academicTerm.orderSequence);
      setAvailableTerms([{ value: academicTerm.id, label: academicTerm.name }]);
    }
  }, [open, academicTerm, academicYear, grade, division]);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const updatedTerms = previousAcademicTerms.map((term) =>
        term.termId === termId
          ? { termId: termId, orderSequence: orderSequence }
          : term
      );

      const payload: AcademicYearTermsRequestBody = {
        academicYearTerms: [
          {
            academicYear,
            grade,
            division,
            terms: updatedTerms
          }
        ]
      };
      await academicTermsApis.upsertAYTerms(school, payload);
      onApiSuccessAndFailure(true, 'edit');
      fetchData();
      onClose();
    } catch (error) {
      console.error('Error updating academic term:', error);
      onApiSuccessAndFailure(false, 'edit');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Edit Term Sequence'
      maxWidth='sm'
      actions={
        <>
          <Button onClick={onClose} color='secondary' variant='outlined'>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color='primary'
            variant='contained'
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Update'
            )}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextInputWithIcon
          label='Academic Year'
          placeholder='Enter Academic Year'
          required
          name='academicYear'
          value={academicYear}
          fullWidth
          disabled
        />
        <TextInputWithIcon
          label='Grade'
          placeholder='Enter Grade'
          required
          name='grade'
          value={grade}
          fullWidth
          disabled
        />
        <TextInputWithIcon
          label='Division'
          placeholder='Enter Division'
          required
          name='division'
          value={division}
          fullWidth
          disabled
        />
        <DropdownSelect
          label='Term'
          placeholder='Select Term'
          required
          name='termId'
          value={termId}
          options={availableTerms}
          onChange={(event) => setTermId(event.target.value as string)}
          disabled
        />
        <TextInputWithIcon
          label='Sequence No.'
          placeholder='Enter Sequence No.'
          name='orderSequence'
          type='number'
          value={orderSequence}
          onChange={(event) => setOrderSequence(Number(event.target.value))}
          fullWidth
          slotProps={{ htmlInput: { min: 0 } }}
        />
      </Box>
    </DialogModal>
  );
};

export default EditAcademicTermModal;
